import { Node, NodeProps } from "@xyflow/react"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { Ban, CirclePlay, Copy, Download, Ellipsis, Plus, Trash } from "lucide-react"
import { useMemo, useState } from "react"
import { useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { useExportWorkflowMutation } from "../../../hooks/useExportWorkflowMutation"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { DuplicateWorkflowDialog } from "../dialogs/DuplicateWorkflowDialog"

export function WorkflowToolbarNode({ data }: NodeProps<Node>) {
  const { t } = useTranslation()
  const { form, container, onAddEventNode, onToggleWorkflowEnabled, onDeleteWorkflow } =
    useWorkflowBuilder()
  const { workflowId = "", width } = data as { workflowId?: string; width?: number }
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false)

  const { isDirty } = useFormState({ control: form.control })
  const { mutate: exportWorkflow } = useExportWorkflowMutation()

  // Duplicate/export hit the backend by persisted id, so they only run on a saved canvas. On a clean
  // canvas every group id equals its persisted Workflow id (see workflowsToFlat).
  const scopeCampaignId = container.id === "global" ? undefined : container.id

  const workflowIndex = useMemo(
    () => form.watch("workflows")?.findIndex((workflow) => workflow.id === workflowId) ?? -1,
    [form, workflowId],
  )
  const name = workflowIndex >= 0 ? (form.watch(`workflows.${workflowIndex}.name`) ?? "") : ""
  const isEnabled = workflowIndex >= 0 ? form.watch(`workflows.${workflowIndex}.isEnabled`) : true

  function onExport() {
    exportWorkflow(
      { workflowId, name },
      { onSuccess: () => toast.success(t("settings.workflows.export.success")) },
    )
  }

  return (
    <>
      <div
        className="nodrag flex items-center justify-between gap-1.5"
        style={{ width }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Input
          value={name}
          onChange={(e) =>
            form.setValue(`workflows.${workflowIndex}.name`, e.target.value, {
              shouldDirty: true,
            })
          }
          placeholder={t("settings.workflows.namePlaceholder")}
          className="w-48 bg-background font-medium shadow-sm"
        />
        <div className="flex items-center gap-1.5">
          <Button variant="outline" onClick={() => onAddEventNode(workflowId)}>
            <Plus className="size-4" />
            {t("campaigns.event.label")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <Ellipsis />
                <span className="sr-only">{t("shared.viewMore")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onToggleWorkflowEnabled(workflowId)}>
                {isEnabled ? <Ban className="size-4" /> : <CirclePlay className="size-4" />}
                <span>
                  {isEnabled ? t("settings.workflows.disable") : t("settings.workflows.enable")}
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={isDirty}
                title={isDirty ? t("settings.workflows.saveFirst") : undefined}
                onClick={() => setIsDuplicateOpen(true)}
              >
                <Copy className="size-4" />
                <span>{t("settings.workflows.duplicate.label")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isDirty}
                title={isDirty ? t("settings.workflows.saveFirst") : undefined}
                onClick={onExport}
              >
                <Download className="size-4" />
                <span>{t("settings.workflows.export.label")}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setIsDeleteOpen(true)}>
                <Trash className="size-4 text-destructive" />
                <span className="text-destructive">{t("shared.delete")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.workflows.delete.title")}</DialogTitle>
            <DialogDescription>{t("settings.workflows.delete.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("shared.cancel")}</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => {
                setIsDeleteOpen(false)
                onDeleteWorkflow(workflowId)
              }}
            >
              {t("settings.workflows.delete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DuplicateWorkflowDialog
        workflowId={workflowId}
        name={name || null}
        open={isDuplicateOpen}
        onOpenChange={setIsDuplicateOpen}
        scopeCampaignId={scopeCampaignId}
      />
    </>
  )
}
